// Right-side profile drawer - Transfer Vessel, Edit Profile, Overview/Documents/History tabs
import React, { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from 'components/ui/sheet';
import { Mail, Phone, MapPin, FileText, Clock, Ship, Edit3, Plus, Download, Trash2, X, Loader2, CreditCard, Calendar } from 'lucide-react';
import CrewStatusBadge from '../../crew-status-badge/CrewStatusBadge';
import { resolveCrewDates } from '../../../utils/resolve-crew-dates';
import { computeCrewStatus, CREW_STATUS } from '../../../utils/compute-crew-status';
import { cn } from 'lib/utils';
import documentsApi from 'api/documents';

/** Check if a date is within 60 days from now */
const isExpiringSoon = (dateStr) => {
  if (!dateStr) return false;
  const diff = new Date(dateStr) - new Date();
  return diff > 0 && diff < 60 * 24 * 60 * 60 * 1000;
};

/** Format ISO date to human-readable: "13 Mar 2026" */
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Format timestamp (Unix ms or ISO) to "14 Apr 2026, 2:37 PM" */
const formatDateTime = (ts) => {
  if (!ts) return '-';
  const d = new Date(ts);
  if (isNaN(d)) return '-';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    + ', ' + d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true });
};

/** Map idType code to readable label */
const ID_TYPE_LABELS = {
  NID: 'National ID', PPN: 'Passport No.', SSN: 'Social Security No.',
  DLN: "Driver's License", IRN: 'Insurance Ref.', CID: 'Company ID',
};

/** Build location string from member fields */
const formatLocation = (member) => {
  const parts = [];
  if (member?.homePort) parts.push(member.homePort);
  if (member?.nationality) parts.push(member.nationality);
  if (parts.length === 0) return '-';
  const base = parts.join(', ');
  return member?.homePort ? `${base} (Home Port)` : base;
};


const ProfileDrawer = ({ open, onClose, member, onUploadDocument, onAssignVessel, onEditMember, refreshKey }) => {
  const { signOnDate: resolvedSignOn, signOffDate: resolvedSignOff } = resolveCrewDates(member);
  const [activeTab, setActiveTab] = useState('overview');
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);

  // Reset tab when drawer opens
  useEffect(() => {
    if (open) setActiveTab('overview');
  }, [open]);

  // Fetch documents when Documents tab is active or refreshKey changes
  const fetchDocuments = useCallback(async () => {
    if (!member?._id) return;
    setDocsLoading(true);
    try {
      const res = await documentsApi.getByEmployeeId(member._id);
      setDocuments(res?.data?.data || []);
    } catch {
      setDocuments([]);
    } finally {
      setDocsLoading(false);
    }
  }, [member?._id]);

  useEffect(() => {
    if (activeTab === 'documents' && open) fetchDocuments();
  }, [activeTab, open, fetchDocuments, refreshKey]);

  const handleDownload = async (docId) => {
    try {
      const res = await documentsApi.getDownloadUrl(docId);
      const url = res?.data?.data?.url;
      if (url) window.open(url, '_blank');
    } catch {
      // silent fail
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentsApi.deleteById(docId);
      setDocuments((prev) => prev.filter((d) => d._id !== docId));
    } catch {
      // silent fail
    }
  };

  if (!member) return null;

  const tabs = ['Overview', 'Documents', 'History'];
  const vesselDisplayName = member?.vesselName || null;
  const hasVessel = !!member?.vesselName;
  const crewStatus = computeCrewStatus(member);

  /** Format document type enum to readable label */
  const formatType = (type) => {
    const map = {
      passport: 'Passport', seamans_book: "Seaman's Book", medical_cert: 'Medical Certificate',
      training_cert: 'Training Certificate', contract: 'Contract', other: 'Other',
    };
    return map[type] || type;
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="tw-w-full sm:tw-w-[480px] tw-p-0 tw-overflow-y-auto tw-flex tw-flex-col" side="right">
        <SheetTitle className="tw-sr-only">Member Profile</SheetTitle>
        <SheetDescription className="tw-sr-only">
          Profile details for {member?.firstName} {member?.lastName}
        </SheetDescription>

        {/* Header */}
        <div className="tw-p-6 tw-border-b tw-border-gray-100 tw-bg-gray-50/50">
          {/* Avatar + Close button in flex row */}
          <div className="tw-flex tw-justify-between tw-items-start tw-mb-4">
            <div className="tw-flex tw-items-center tw-justify-center tw-h-16 tw-w-16 tw-rounded-full tw-bg-indigo-500 tw-text-white tw-text-xl tw-font-bold tw-flex-shrink-0">
              {member?.firstName?.charAt(0)}{member?.lastName?.charAt(0)}
            </div>
            <button
              onClick={() => onClose(false)}
              className="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-rounded-full tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>
          </div>
          <h2 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-m-0">
            {member?.firstName} {member?.lastName}
          </h2>
          <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-500 tw-mt-1">
            <span className="tw-font-medium tw-text-gray-700">{member?.designation || 'No rank assigned'}</span>
            {member?.departmentName && (
              <>
                <span>&bull;</span>
                <span>{member.departmentName}</span>
              </>
            )}
          </div>
          <div className="tw-flex tw-gap-2 tw-mt-4">
            <button
              onClick={() => onAssignVessel && onAssignVessel(member)}
              className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-indigo-600 tw-border-0 tw-rounded-lg tw-cursor-pointer tw-shadow-sm hover:tw-bg-indigo-700 tw-transition-colors"
            >
              <Ship className="tw-w-4 tw-h-4" />
              {hasVessel ? 'Transfer Vessel' : 'Assign Vessel'}
            </button>
            <button
              onClick={() => onEditMember && onEditMember(member)}
              className="tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-cursor-pointer tw-shadow-sm hover:tw-bg-gray-50 tw-transition-colors"
            >
              <Edit3 className="tw-w-4 tw-h-4" />
              Edit Profile
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="tw-flex tw-border-b tw-border-gray-200 tw-px-6">
          {tabs.map((tab) => {
            const tabKey = tab.toLowerCase();
            return (
              <button
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={cn(
                  'tw-px-4 tw-pb-3 tw-pt-4 tw-text-sm tw-font-medium tw-border-0 tw-border-b-2 tw-cursor-pointer tw-bg-transparent tw-transition-colors',
                  activeTab === tabKey
                    ? 'tw-border-indigo-600 tw-text-indigo-600'
                    : 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700'
                )}
              >
                {tab}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tw-space-y-6">
              <Section title="Contact Information">
                <InfoRow icon={Mail} value={member?.email || '-'} />
                <InfoRow icon={Phone} value={member?.phone || 'No phone added'} />
                <InfoRow icon={MapPin} value={formatLocation(member)} />
              </Section>
              <Section title="Identification">
                {(member?.idNo || member?.passNo) ? (
                  <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-border tw-border-gray-100 tw-flex tw-items-start tw-gap-3">
                    <div className="tw-p-2 tw-bg-indigo-50 tw-text-indigo-600 tw-rounded-lg tw-mt-0.5">
                      <CreditCard className="tw-w-4 tw-h-4" />
                    </div>
                    <div className="tw-flex-1">
                      <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0">
                        {member?.idNo || member?.passNo}
                      </p>
                      <p className="tw-text-xs tw-text-gray-500 tw-m-0 tw-mt-0.5">
                        {ID_TYPE_LABELS[member?.idType] || (member?.passNo ? 'Passport No.' : 'ID Number')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-border tw-border-dashed tw-border-gray-300 tw-text-center">
                    <CreditCard className="tw-w-5 tw-h-5 tw-text-gray-300 tw-mx-auto tw-mb-1.5" />
                    <p className="tw-text-xs tw-text-gray-400 tw-m-0">No ID on file</p>
                    <button
                      onClick={() => onUploadDocument && onUploadDocument(member)}
                      className="tw-text-xs tw-text-indigo-600 tw-font-medium hover:tw-underline tw-mt-1 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                    >
                      Add ID
                    </button>
                  </div>
                )}
              </Section>
              <Section title="Current Assignment">
                <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-border tw-border-gray-100">
                  <div className="tw-flex tw-justify-between tw-items-start">
                    <div>
                      <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0">
                        {vesselDisplayName || 'Unassigned'}
                      </p>
                      <p className="tw-text-xs tw-text-gray-500 tw-m-0 tw-mt-0.5">Vessel Name</p>
                    </div>
                    {crewStatus === CREW_STATUS.ONBOARD && (
                      <span className="tw-text-xs tw-font-medium tw-text-emerald-700 tw-bg-emerald-50 tw-px-2 tw-py-0.5 tw-rounded-full tw-border tw-border-emerald-200">
                        Onboard
                      </span>
                    )}
                    {crewStatus === CREW_STATUS.ON_LEAVE && (
                      <span className="tw-text-xs tw-font-medium tw-text-amber-700 tw-bg-amber-50 tw-px-2 tw-py-0.5 tw-rounded-full tw-border tw-border-amber-200">
                        On Leave
                      </span>
                    )}
                  </div>
                  <div className="tw-mt-4 tw-pt-4 tw-border-t tw-border-gray-200 tw-grid tw-grid-cols-2 tw-gap-4">
                    <div>
                      <p className="tw-text-xs tw-text-gray-500 tw-m-0">Sign On Date</p>
                      <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-m-0 tw-mt-0.5">
                        {formatDate(resolvedSignOn)}
                      </p>
                    </div>
                    <div>
                      <p className="tw-text-xs tw-text-gray-500 tw-m-0">Expected Sign Off</p>
                      <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-m-0 tw-mt-0.5">
                        {formatDate(resolvedSignOff)}
                      </p>
                    </div>
                  </div>
                </div>
              </Section>
              <Section title="Joined Bedrock">
                <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-border tw-border-gray-100 tw-flex tw-items-start tw-gap-3">
                  <div className="tw-p-2 tw-bg-indigo-50 tw-text-indigo-600 tw-rounded-lg tw-mt-0.5">
                    <Calendar className="tw-w-4 tw-h-4" />
                  </div>
                  <div>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0">
                      {formatDateTime(member?.createdAt)}
                    </p>
                    <p className="tw-text-xs tw-text-gray-500 tw-m-0 tw-mt-0.5">Date Added</p>
                  </div>
                </div>
              </Section>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="tw-space-y-4">
              <div className="tw-flex tw-justify-between tw-items-center tw-mb-2">
                <h3 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-m-0">Certificates & Documents</h3>
                <button
                  onClick={() => onUploadDocument && onUploadDocument(member)}
                  className="tw-text-xs tw-text-indigo-600 tw-font-medium hover:tw-underline tw-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-cursor-pointer"
                >
                  <Plus className="tw-w-3 tw-h-3" /> Upload
                </button>
              </div>

              {docsLoading ? (
                <div className="tw-flex tw-justify-center tw-py-8">
                  <Loader2 className="tw-w-6 tw-h-6 tw-animate-spin tw-text-gray-400" />
                </div>
              ) : documents.length > 0 ? (
                documents.map((doc) => {
                  const expiringSoon = isExpiringSoon(doc.expiryDate);
                  return (
                    <div key={doc._id} className="tw-flex tw-items-center tw-justify-between tw-p-3 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-border-indigo-200 hover:tw-bg-indigo-50/20 tw-transition-colors tw-group">
                      <div className="tw-flex tw-items-center tw-gap-3">
                        <div className="tw-p-2 tw-bg-blue-50 tw-text-blue-600 tw-rounded">
                          <FileText className="tw-w-4 tw-h-4" />
                        </div>
                        <div>
                          <p className="tw-text-sm tw-font-medium tw-text-gray-900 tw-m-0">{doc.name}</p>
                          <p className="tw-text-xs tw-text-gray-500 tw-m-0">
                            {formatType(doc.type)}
                            {doc.expiryDate ? ` \u2022 Exp: ${new Date(doc.expiryDate).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="tw-flex tw-items-center tw-gap-2">
                        {expiringSoon && (
                          <span className="tw-px-2 tw-py-0.5 tw-bg-amber-50 tw-text-amber-700 tw-text-[10px] tw-font-bold tw-uppercase tw-rounded-full tw-border tw-border-amber-200">
                            Expiring
                          </span>
                        )}
                        <button
                          onClick={() => handleDownload(doc._id)}
                          className="tw-text-gray-400 hover:tw-text-indigo-600 tw-transition-colors tw-bg-transparent tw-border-0 tw-cursor-pointer"
                        >
                          <Download className="tw-w-4 tw-h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(doc._id)}
                          className="tw-text-gray-400 hover:tw-text-red-600 tw-transition-colors tw-bg-transparent tw-border-0 tw-cursor-pointer tw-opacity-0 group-hover:tw-opacity-100"
                        >
                          <Trash2 className="tw-w-4 tw-h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="tw-text-center tw-py-8 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-dashed tw-border-gray-300">
                  <FileText className="tw-w-8 tw-h-8 tw-text-gray-300 tw-mx-auto tw-mb-2" />
                  <p className="tw-text-sm tw-text-gray-500 tw-m-0">No documents uploaded</p>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="tw-text-center tw-py-12">
              <div className="tw-inline-flex tw-p-3 tw-bg-gray-100 tw-rounded-full tw-text-gray-400 tw-mb-3">
                <Clock className="tw-w-6 tw-h-6" />
              </div>
              <p className="tw-text-sm tw-text-gray-500 tw-m-0">No recent activity history available.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Section with uppercase label
const Section = ({ title, children }) => (
  <section>
    <h3 className="tw-text-xs tw-font-bold tw-text-gray-400 tw-uppercase tw-tracking-wider tw-mb-3 tw-m-0">
      {title}
    </h3>
    <div className="tw-space-y-3">{children}</div>
  </section>
);

// Icon + value row (for contact info)
const InfoRow = ({ icon: Icon, value }) => (
  <div className="tw-flex tw-items-center tw-gap-3 tw-text-sm">
    <Icon className="tw-h-4 tw-w-4 tw-text-gray-400 tw-flex-shrink-0" />
    <span className="tw-text-gray-900">{value}</span>
  </div>
);

export default ProfileDrawer;
